import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PageContainer from 'src/components/PageContainer';
import PageContent from 'src/components/PageContent';
import NavBar from '../NavBar';
import Button from 'src/components/Button';
import ImageViewer from 'src/components/ImageViewer';
import Icon from 'src/components/Icon';
import StatusBadge, { StatusBadgeColors } from 'src/components/StatusBadge';
import TabNav from 'src/components/TabNav';
import UserStore from 'src/stores/UserStore';
import { ICONS } from 'src/utils/constants';
import * as variables from 'src/styles/variables';
import { useDocumentScanLlmTrace, type DocumentScan as DocumentScanRecord } from 'src/queries/useDocumentScans';
import {
    flattenExtractedDataFields,
    getDocumentScanEncounterFormInputPlan,
    getEncounterFieldLabel,
    getDocumentScanEntityUpdateSummary,
    getDocumentScanSlotSummary,
    getDocumentScanStatus,
    getDocumentScanTypeDisplayName,
    getDocumentScanTypeKey,
} from 'src/utils/documentScans';
import LlmTraceModal from './LlmTraceModal';
import styles from './styles.module.scss';

function getStatusBadgeColor(status: string) {
    if (status === 'complete' || status === 'extracted' || status === 'uploaded') {
        return StatusBadgeColors.Green;
    }
    if (status === 'failed') {
        return StatusBadgeColors.Red;
    }
    return StatusBadgeColors.Yellow;
}

type OcrRegion = {
    id: string;
    text: string;
    x: number;
    y: number;
    w: number;
    h: number;
    confidence: number | null;
};

type OcrTooltipState = {
    id: string;
    text: string;
    left: number;
    top: number;
    placement: 'above' | 'below';
};

type TypedDetailPanel = 'extracted' | 'mapping' | 'settings';
type MappingStatus = 'mapped' | 'applied' | 'proposed' | 'failed' | 'skipped' | 'created' | 'linked';

const OCR_TOOLTIP_MAX_WIDTH = 280;
const OCR_TOOLTIP_EDGE_PADDING = 8;

function formatValue(value: any): string {
    if (value === null || value === undefined || value === '') {
        return 'N/A';
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
}

function formatOcrRegionCount(count: number): string {
    return `${count} OCR ${count === 1 ? 'region' : 'regions'}`;
}

function formatEntityType(entityType: any): string {
    if (entityType === 'encounter_form') {
        return 'Encounter form';
    }
    if (entityType === 'encounter') {
        return 'Encounter';
    }
    return getEncounterFieldLabel(entityType);
}

function formatRelationshipType(relationshipType: any): string {
    if (relationshipType === 'created_entity') {
        return 'created';
    }
    if (relationshipType === 'attached_document') {
        return 'linked';
    }
    return relationshipType || 'linked';
}

function formatMappingTarget(entity: any): string {
    const identifier = formatValue(entity.entityIdentifier);

    if (identifier === 'N/A') {
        return formatEntityType(entity.entityType);
    }

    return `${formatEntityType(entity.entityType)} #${identifier}`;
}

function getRelationshipStatus(relationshipType: any): MappingStatus {
    if (relationshipType === 'created_entity') {
        return 'created';
    }
    return 'linked';
}

function getMappingStatusIconName(status: MappingStatus): string {
    switch (status) {
        case 'created':
            return ICONS.PlusButton;
        case 'applied':
            return ICONS.CheckCircle;
        case 'proposed':
            return ICONS.TimeClockCircle1Alternate;
        case 'failed':
        case 'skipped':
            return ICONS.Close;
        case 'linked':
            return ICONS.Attachment;
        default:
            return ICONS.SynchronizeArrow1;
    }
}

function getMappingStatusLabel(status: MappingStatus): string {
    switch (status) {
        case 'created':
            return 'Created';
        case 'applied':
            return 'Applied';
        case 'proposed':
            return 'Proposed';
        case 'failed':
            return 'Failed';
        case 'skipped':
            return 'Skipped';
        case 'linked':
            return 'Linked';
        default:
            return 'Mapped';
    }
}

function getMappingStatusTitle(status: MappingStatus, reason?: string | null): string {
    const label = getMappingStatusLabel(status);
    return reason ? `${label}: ${reason}` : label;
}

function getEncounterUpdateMappingStatus(status: string): MappingStatus {
    if (status === 'applied' || status === 'proposed' || status === 'failed') {
        return status;
    }
    return 'mapped';
}

function renderMappingStatusIcon(status: MappingStatus, reason?: string | null) {
    const label = getMappingStatusTitle(status, reason);
    return (
        <span className={styles.mappingStatusIcon} title={label} aria-label={label}>
            <Icon name={getMappingStatusIconName(status)} size={14} color={variables.darkBlue} />
        </span>
    );
}

function getDocumentScanSupportedEntityType(documentScan: DocumentScanRecord): string {
    return `${documentScan.documentScanTypeDetail?.supportedEntityType || (documentScan as any).documentTypeTarget || ''}`;
}

function formatEncounterFormInputValue(inputSet: any): string {
    const valueSetLabel = inputSet?.valueSetDetail?.label;
    const value = inputSet?.value;
    if (valueSetLabel) {
        return value === null || value === undefined || value === '' ? valueSetLabel : `${valueSetLabel} (${formatValue(value)})`;
    }
    if (value !== null && value !== undefined && value !== '') {
        return formatValue(value);
    }
    if (inputSet?.action === 'press') {
        return 'Press button';
    }
    return 'N/A';
}

function formatEncounterFormInputLocation(inputSet: any): string {
    const parts = [inputSet?.targetPageName || inputSet?.pageName || inputSet?.pageRef, inputSet?.inputMethodType]
        .filter(Boolean)
        .map(String);
    return parts.join(' / ');
}

function formatEncounterFormSource(inputSet: any): string | null {
    const parts = [];
    if (inputSet?.sourceText) {
        parts.push(`Source: ${inputSet.sourceText}`);
    }
    if (inputSet?.confidence !== null && inputSet?.confidence !== undefined) {
        parts.push(`Confidence: ${Math.round(Number(inputSet.confidence) * 100)}%`);
    }
    return parts.length ? parts.join(' / ') : null;
}

function getEncounterMappingRows(entityDataFields: Array<{ label: string; value: string }>, entityUpdateSummary: any) {
    const updatedFields = Array.isArray(entityUpdateSummary.updatedFields) ? entityUpdateSummary.updatedFields : [];
    const consumedUpdatedIndexes = new Set<number>();

    const rows = entityDataFields.map((field) => {
        const updatedFieldIndex = updatedFields.findIndex((updatedField: any, index: number) => {
            if (consumedUpdatedIndexes.has(index)) {
                return false;
            }
            const updatedLabel = `${updatedField.label || getEncounterFieldLabel(updatedField.targetPath)}`;
            const targetLabel = updatedField.targetPath ? getEncounterFieldLabel(updatedField.targetPath) : '';
            return updatedLabel === field.label || targetLabel === field.label;
        });
        const updatedField = updatedFieldIndex >= 0 ? updatedFields[updatedFieldIndex] : null;
        if (updatedFieldIndex >= 0) {
            consumedUpdatedIndexes.add(updatedFieldIndex);
        }

        return {
            label: field.label,
            value: field.value,
            status: updatedField ? getEncounterUpdateMappingStatus(entityUpdateSummary.status) : ('mapped' as MappingStatus),
            reason: updatedField ? entityUpdateSummary.errorMessage : null,
        };
    });

    updatedFields.forEach((updatedField: any, index: number) => {
        if (consumedUpdatedIndexes.has(index)) {
            return;
        }
        rows.push({
            label: `${updatedField.label || getEncounterFieldLabel(updatedField.targetPath)}`,
            value: formatValue(updatedField.value),
            status: getEncounterUpdateMappingStatus(entityUpdateSummary.status),
            reason: entityUpdateSummary.errorMessage || null,
        });
    });

    return rows;
}

function formatVersionWithId(version: any, id: any): string {
    if (!version && !id) {
        return 'N/A';
    }

    const versionLabel = version ? `v${version}` : 'Version N/A';
    return id ? `${versionLabel} / ID: ${id}` : versionLabel;
}

function getAssetLabel(asset: DocumentScanRecord['assets'][0], index: number) {
    const metadata = asset.assetMetadata || {};
    return metadata.displayName || metadata.slotKey || (metadata.pageNumber ? `Page ${metadata.pageNumber}` : `Image ${index + 1}`);
}

function getOcrText(asset?: DocumentScanRecord['assets'][0] | null): string {
    if (!asset?.content) {
        return '';
    }
    return typeof asset.content === 'string' ? asset.content : JSON.stringify(asset.content, null, 2);
}

function parseOcrRegions(ocrText: string): OcrRegion[] {
    if (!ocrText?.trim()) {
        return [];
    }

    const regionRegex =
        /^(.*?)\s*\[ocrBlockId:\s*([^,\]]+),\s*x:\s*([\d.]+),\s*y:\s*([\d.]+),\s*w:\s*([\d.]+),\s*h:\s*([\d.]+),\s*c:\s*([\d.]+)\]/;

    return ocrText
        .split(/\r?\n/)
        .map((line, index) => {
            const match = line.match(regionRegex);
            if (!match) {
                return null;
            }

            return {
                id: match[2] || `ocr-${index}`,
                text: match[1]?.trim() || '',
                x: Number(match[3]),
                y: Number(match[4]),
                w: Number(match[5]),
                h: Number(match[6]),
                confidence: match[7] ? Number(match[7]) : null,
            };
        })
        .filter(Boolean) as OcrRegion[];
}

function toPercent(value: number, total: number): number {
    if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0) {
        return 0;
    }

    return value <= 1 ? value * 100 : (value / total) * 100;
}

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(value, max));
}

function getOcrAssetForImage(assets: DocumentScanRecord['assets'], imageAsset?: DocumentScanRecord['assets'][0]) {
    const ocrAssets = (assets || []).filter((asset) => asset.assetType === 'ocr_data' && asset.status === 'uploaded');
    if (!imageAsset) {
        return ocrAssets[0] || null;
    }

    const imageMetadata = imageAsset.assetMetadata || {};
    return (
        ocrAssets.find((asset) => asset.assetMetadata?.sourceDocumentScanAssetId === imageAsset.documentScanAssetId) ||
        ocrAssets.find(
            (asset) =>
                asset.assetMetadata?.pageNumber &&
                imageMetadata.pageNumber &&
                `${asset.assetMetadata.pageNumber}` === `${imageMetadata.pageNumber}`,
        ) ||
        ocrAssets[0] ||
        null
    );
}

function getSchemaProperties(schema: any): Record<string, any> {
    if (!schema || typeof schema !== 'object') {
        return {};
    }

    if (schema.properties && typeof schema.properties === 'object') {
        return schema.properties;
    }

    return {};
}

function flattenSchemaDataFields(
    data: any,
    schema: any,
    prefix = '',
): Array<{ label: string; value: string; key: string }> {
    if (!data || typeof data !== 'object') {
        return [];
    }

    const properties = getSchemaProperties(schema);
    const orderedKeys = [
        ...Object.keys(properties).filter((key) => Object.prototype.hasOwnProperty.call(data, key)),
        ...Object.keys(data).filter((key) => !Object.prototype.hasOwnProperty.call(properties, key)),
    ];

    return orderedKeys.flatMap((key) => {
        const value = data[key];
        if (value === null || value === undefined || value === '') {
            return [];
        }

        const fieldSchema = properties[key] || {};
        const labelBase = fieldSchema.title || getEncounterFieldLabel(key);
        const label = prefix ? `${prefix} ${labelBase}` : labelBase;

        if (Array.isArray(value)) {
            return value.map((item, index) => ({
                key: `${key}-${index}`,
                label: `${label} ${index + 1}`,
                value: formatValue(item),
            }));
        }

        if (typeof value === 'object') {
            return flattenSchemaDataFields(value, fieldSchema, label);
        }

        return [{ key, label, value: formatValue(value) }];
    });
}

function getLatestTraceMetadata(documentScan: DocumentScanRecord): Record<string, any> {
    const traces = Array.isArray(documentScan.scanMetadata?.llmTraces) ? documentScan.scanMetadata.llmTraces : [];
    return (traces[traces.length - 1] || {}) as Record<string, any>;
}

export default function TypedDocumentScanDetail(props: { documentScan: DocumentScanRecord; onBack: () => void }) {
    const { documentScan, onBack } = props;
    const userStore = useContext(UserStore);
    const assets = documentScan.assets || [];
    const imageAssets = assets.filter((asset) => asset.assetType === 'image');
    const imageFrameRef = useRef<HTMLDivElement>(null);
    const [selectedImageAssetId, setSelectedImageAssetId] = useState<string | null>(
        imageAssets[0]?.documentScanAssetId || null,
    );
    const [showOcrOverlay, setShowOcrOverlay] = useState(false);
    const [ocrTooltip, setOcrTooltip] = useState<OcrTooltipState | null>(null);
    const [selectedPanel, setSelectedPanel] = useState<TypedDetailPanel>('extracted');
    const [traceModalOpen, setTraceModalOpen] = useState(false);
    const traceQuery = useDocumentScanLlmTrace(documentScan.documentScanId, traceModalOpen);

    const selectedImage =
        imageAssets.find((asset) => asset.documentScanAssetId === selectedImageAssetId) || imageAssets[0];
    const selectedImageIndex = Math.max(
        0,
        imageAssets.findIndex((asset) => asset.documentScanAssetId === selectedImage?.documentScanAssetId),
    );
    const selectedOcrAsset = getOcrAssetForImage(assets, selectedImage);
    const selectedOcrText = getOcrText(selectedOcrAsset);
    const ocrRegions = useMemo(() => parseOcrRegions(selectedOcrText), [selectedOcrText]);
    const scanStatus = getDocumentScanStatus(documentScan);
    const scanDisplayName = getDocumentScanTypeDisplayName(documentScan);
    const scanTypeKey = getDocumentScanTypeKey(documentScan);
    const typeDetail = documentScan.documentScanTypeDetail || {};
    const traceMetadata = getLatestTraceMetadata(documentScan);
    const canViewTrace =
        (userStore.isGraphiumAdmin() || userStore.roleNames?.includes('GRAPHIUM_ADMIN')) &&
        !!documentScan.llmTraceAvailable;
    const extractionSchema = (typeDetail.extractionSchema as any) || null;
    const extractedFields = flattenSchemaDataFields(documentScan.extractedData, extractionSchema);
    const entityUpdateSummary = getDocumentScanEntityUpdateSummary(documentScan);
    const entityDataFields = flattenExtractedDataFields(documentScan.entityData || documentScan.scanMetadata?.entityData);
    const encounterMappingRows = getEncounterMappingRows(entityDataFields, entityUpdateSummary);
    const entities = ((documentScan as any).entities || []) as any[];
    const isEncounterFormScan = getDocumentScanSupportedEntityType(documentScan) === 'encounter_form';
    const typedDetailTabs = useMemo(
        () => [
            { key: 'extracted' as TypedDetailPanel, label: 'Extracted Data' },
            { key: 'mapping' as TypedDetailPanel, label: isEncounterFormScan ? 'Form Mapping' : 'Encounter Mapping' },
            { key: 'settings' as TypedDetailPanel, label: 'Scan Settings' },
        ],
        [isEncounterFormScan],
    );
    const encounterFormInputPlan = getDocumentScanEncounterFormInputPlan(documentScan);
    const formRecordEntities = entities.filter((entity) => entity.entityType === 'encounter_form');
    const contextEntities = isEncounterFormScan ? entities.filter((entity) => entity.entityType !== 'encounter_form') : [];
    const mappingEntities = isEncounterFormScan ? formRecordEntities : entities;
    const inputApplicationStatus = encounterFormInputPlan.inputSetApplication?.status;
    const provider = traceMetadata.provider || documentScan.scanMetadata?.structuredOutputProvider || 'N/A';
    const model =
        traceMetadata.model ||
        documentScan.scanMetadata?.structuredOutputModel ||
        documentScan.scanMetadata?.modelName ||
        'N/A';
    const setupFields = [
        { label: 'Document type', value: scanDisplayName },
        { label: 'Type key', value: scanTypeKey },
        {
            label: 'Org default version',
            value: formatVersionWithId(
                typeDetail.documentScanTypeVersionNumber || documentScan.documentScanTypeVersionNumber,
                documentScan.documentScanTypeVersionId,
            ),
        },
        {
            label: 'Facility override',
            value: typeDetail.facilityOverrideApplied ? 'Applied' : 'Not applied',
        },
        {
            label: 'Override version',
            value: typeDetail.facilityOverrideApplied
                ? formatVersionWithId(
                      typeDetail.facilityDocumentScanTypeVersionNumber ||
                          documentScan.facilityDocumentScanTypeVersionNumber,
                      documentScan.facilityDocumentScanTypeVersionId,
                  )
                : 'N/A',
        },
        { label: 'Provider', value: provider },
        { label: 'Model', value: model },
        ...(isEncounterFormScan && encounterFormInputPlan.selectedFormAssociation
            ? [
                  {
                      label: 'Form definition',
                      value: formatVersionWithId(
                          encounterFormInputPlan.selectedFormAssociation.formDefinitionVersion,
                          encounterFormInputPlan.selectedFormAssociation.formDefinitionId,
                      ),
                  },
              ]
            : []),
        ...(isEncounterFormScan && contextEntities.length
            ? [
                  {
                      label: 'Parent context',
                      value: contextEntities
                          .map((entity) => `${formatEntityType(entity.entityType)} ${entity.entityIdentifier}`)
                          .join(', '),
                  },
              ]
            : []),
        { label: 'Capture mode', value: typeDetail.captureMode || documentScan.scanMetadata?.captureMode || 'N/A' },
        { label: 'Created by', value: documentScan.createdByDisplayName || 'N/A' },
        { label: 'Scan ID', value: documentScan.documentScanId },
    ];
    useEffect(() => {
        if (!selectedImageAssetId && imageAssets[0]?.documentScanAssetId) {
            setSelectedImageAssetId(imageAssets[0].documentScanAssetId);
        }
    }, [imageAssets, selectedImageAssetId]);

    useEffect(() => {
        setOcrTooltip(null);
    }, [showOcrOverlay, selectedImage?.documentScanAssetId]);

    const selectRelativeImage = (delta: number) => {
        if (!imageAssets.length) {
            return;
        }

        const nextIndex = Math.max(0, Math.min(imageAssets.length - 1, selectedImageIndex + delta));
        setSelectedImageAssetId(imageAssets[nextIndex].documentScanAssetId);
    };

    const showOcrTooltip = (region: OcrRegion, element: HTMLElement) => {
        if (!region.text || !imageFrameRef.current) {
            setOcrTooltip(null);
            return;
        }

        const frameRect = imageFrameRef.current.getBoundingClientRect();
        const regionRect = element.getBoundingClientRect();
        const maxLeft = Math.max(OCR_TOOLTIP_EDGE_PADDING, frameRect.width - OCR_TOOLTIP_MAX_WIDTH - OCR_TOOLTIP_EDGE_PADDING);
        const left = clamp(regionRect.left - frameRect.left, OCR_TOOLTIP_EDGE_PADDING, maxLeft);
        const regionTop = regionRect.top - frameRect.top;
        const hasRoomAbove = regionTop > 56;

        setOcrTooltip({
            id: region.id,
            text: region.text,
            left,
            top: hasRoomAbove ? regionTop - OCR_TOOLTIP_EDGE_PADDING : regionRect.bottom - frameRect.top + OCR_TOOLTIP_EDGE_PADDING,
            placement: hasRoomAbove ? 'above' : 'below',
        });
    };

    const renderOcrOverlay = (imageSize: { naturalWidth: number; naturalHeight: number }) => {
        if (!showOcrOverlay) {
            return null;
        }

        return ocrRegions.map((region) => (
            <div
                key={region.id}
                data-test-id={`ocr-region-${region.id}`}
                className={styles.ocrRegion}
                aria-label={region.text ? `OCR text: ${region.text}` : 'OCR region'}
                tabIndex={0}
                onMouseEnter={(event) => showOcrTooltip(region, event.currentTarget)}
                onMouseLeave={() => setOcrTooltip(null)}
                onFocus={(event) => showOcrTooltip(region, event.currentTarget)}
                onBlur={() => setOcrTooltip(null)}
                onMouseDown={(event) => event.stopPropagation()}
                onTouchStart={(event) => event.stopPropagation()}
                style={{
                    left: `${toPercent(region.x, imageSize.naturalWidth)}%`,
                    top: `${toPercent(region.y, imageSize.naturalHeight)}%`,
                    width: `${toPercent(region.w, imageSize.naturalWidth)}%`,
                    height: `${toPercent(region.h, imageSize.naturalHeight)}%`,
                }}
            />
        ));
    };

    return (
        <PageContainer className={styles.typedPageContainer} allowScaling hideFooter>
            <NavBar />
            <PageContent className={styles.typedPageContent}>
                <div className={styles.typedShell}>
                    <div className={styles.typedHeader} data-test-id="typed-document-scan-detail">
                        <button className={styles.typedBackButton} onClick={onBack} aria-label="Back to document scans">
                            {'<'}
                        </button>
                        <div className={styles.typedTitleBlock}>
                            <h1 className={styles.typedTitle}>{scanDisplayName}</h1>
                            <div className={styles.typedMetaLine}>
                                <span>{scanTypeKey}</span>
                                <span>{documentScan.documentScanId}</span>
                                <span>{getDocumentScanSlotSummary(documentScan)}</span>
                            </div>
                        </div>
                        <div className={styles.typedHeaderActions}>
                            <StatusBadge status={getStatusBadgeColor(scanStatus)} text={scanStatus.toUpperCase()} />
                            {documentScan.archived && <StatusBadge status={StatusBadgeColors.Yellow} text="ARCHIVED" />}
                        </div>
                    </div>

                    <div className={styles.typedWorkbench} data-test-id="typed-document-scan-workbench">
                        <section className={styles.viewerPane}>
                            <div className={styles.viewerToolbar}>
                                <div className={styles.pageStepper}>
                                    <button
                                        className={styles.iconControl}
                                        onClick={() => selectRelativeImage(-1)}
                                        disabled={selectedImageIndex === 0}
                                        aria-label="Previous image"
                                    >
                                        {'<'}
                                    </button>
                                    <span>
                                        {imageAssets.length ? selectedImageIndex + 1 : 0} / {imageAssets.length}
                                    </span>
                                    <button
                                        className={styles.iconControl}
                                        onClick={() => selectRelativeImage(1)}
                                        disabled={selectedImageIndex >= imageAssets.length - 1}
                                        aria-label="Next image"
                                    >
                                        {'>'}
                                    </button>
                                </div>
                                <div className={styles.ocrToolbarControls}>
                                    <Button
                                        type={showOcrOverlay ? 'primary' : 'outline'}
                                        text={showOcrOverlay ? 'Hide OCR' : 'Show OCR'}
                                        onClick={() => setShowOcrOverlay((value) => !value)}
                                        disabled={!ocrRegions.length}
                                        className={styles.compactButton}
                                    />
                                    {showOcrOverlay && (
                                        <span className={styles.ocrSummary}>
                                            {formatOcrRegionCount(ocrRegions.length)}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className={styles.typedImageFrame} ref={imageFrameRef} data-test-id="typed-image-frame">
                                {selectedImage?.viewUrl ? (
                                    <ImageViewer
                                        imageUrl={selectedImage.viewUrl}
                                        alt={getAssetLabel(selectedImage, selectedImageIndex)}
                                        overlay={renderOcrOverlay}
                                    />
                                ) : (
                                    <div className={styles.emptyState}>No image asset found</div>
                                )}
                                {ocrTooltip && (
                                    <div
                                        className={
                                            ocrTooltip.placement === 'above'
                                                ? styles.ocrTooltipAbove
                                                : styles.ocrTooltipBelow
                                        }
                                        data-test-id={`ocr-tooltip-${ocrTooltip.id}`}
                                        style={{
                                            left: ocrTooltip.left,
                                            top: ocrTooltip.top,
                                        }}
                                    >
                                        {ocrTooltip.text}
                                    </div>
                                )}
                            </div>

                            {imageAssets.length > 1 && (
                                <div className={styles.thumbnailRail}>
                                    {imageAssets.map((asset, index) => (
                                        <button
                                            key={asset.documentScanAssetId}
                                            className={
                                                asset.documentScanAssetId === selectedImage?.documentScanAssetId
                                                    ? styles.thumbnailButtonActive
                                                    : styles.thumbnailButton
                                            }
                                            onClick={() => setSelectedImageAssetId(asset.documentScanAssetId)}
                                        >
                                            {getAssetLabel(asset, index)}
                                        </button>
                                    ))}
                                </div>
                            )}

                        </section>

                        <aside className={styles.typedDetailPane} data-test-id="typed-document-scan-detail-pane">
                            <div className={styles.detailTabs}>
                                <div className={styles.detailTabNav}>
                                    <TabNav
                                        tabs={typedDetailTabs.map((tab) => tab.label)}
                                        selectedTabIndex={typedDetailTabs.findIndex((tab) => tab.key === selectedPanel)}
                                        onTabClick={(tabName: string) => {
                                            setSelectedPanel(
                                                typedDetailTabs.find((tab) => tab.label === tabName)?.key || 'extracted',
                                            );
                                        }}
                                    />
                                </div>
                                {canViewTrace && (
                                    <button
                                        type="button"
                                        aria-label="Open LLM trace"
                                        title="LLM Trace"
                                        onClick={() => setTraceModalOpen(true)}
                                        className={styles.traceIconButton}
                                    >
                                        <Icon name={ICONS.NotesBookText} size={16} color={variables.darkBlue} />
                                    </button>
                                )}
                            </div>

                            {selectedPanel === 'extracted' && (
                                <div className={styles.detailPanel} data-test-id="typed-extracted-panel">
                                    <div className={styles.panelHeaderRow}>
                                        <h2>Extracted Data</h2>
                                        <span>{extractedFields.length} fields</span>
                                    </div>
                                    {extractedFields.length ? (
                                        <div className={styles.typedFieldGrid}>
                                            {extractedFields.map((field, index) => (
                                                <div className={styles.typedFieldRow} key={`${field.key}-${index}`}>
                                                    <div className={styles.typedFieldLabel}>{field.label}</div>
                                                    <div className={styles.typedFieldValue}>{field.value}</div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className={styles.typedEmpty}>No extracted data was returned.</div>
                                    )}
                                </div>
                            )}

                            {selectedPanel === 'mapping' && (
                                <div className={styles.detailPanel} data-test-id="typed-mappings-panel">
                                    <div className={styles.panelHeaderRow}>
                                        <h2>{isEncounterFormScan ? 'Form Mapping' : 'Encounter Mapping'}</h2>
                                    </div>
                                    {!!mappingEntities.length && (
                                        <div className={styles.mappingList}>
                                            {mappingEntities.map((entity, index) => (
                                                <div className={styles.mappingRow} key={entity.documentScanEntityId || index}>
                                                    <div>
                                                        <strong>{formatMappingTarget(entity)}</strong>
                                                        <span>{formatRelationshipType(entity.relationshipType)}</span>
                                                    </div>
                                                    {renderMappingStatusIcon(
                                                        getRelationshipStatus(entity.relationshipType),
                                                        formatRelationshipType(entity.relationshipType),
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {isEncounterFormScan ? (
                                        <>
                                            {inputApplicationStatus && (
                                                <div className={styles.inputSetSummary}>
                                                    Input application status: {formatValue(inputApplicationStatus)}
                                                </div>
                                            )}
                                            {encounterFormInputPlan.inputSets.length > 0 && (
                                                <>
                                                    <h3 className={styles.subPanelTitle}>Input Values</h3>
                                                    <div className={styles.mappingFieldGrid}>
                                                        {encounterFormInputPlan.inputSets.map((inputSet: any, index: number) => (
                                                            <div className={styles.typedFieldRow} key={`input-set-${index}`}>
                                                                <div className={styles.typedFieldLabel}>
                                                                    <div className={styles.inputSetLabel}>
                                                                        <strong>
                                                                            {inputSet.inputTitle ||
                                                                                getEncounterFieldLabel(inputSet.inputName)}
                                                                        </strong>
                                                                        <span>{formatEncounterFormInputLocation(inputSet)}</span>
                                                                    </div>
                                                                </div>
                                                                <div className={styles.typedFieldValue}>
                                                                    <div>{formatEncounterFormInputValue(inputSet)}</div>
                                                                    {formatEncounterFormSource(inputSet) && (
                                                                        <span className={styles.inputSetMeta}>
                                                                            {formatEncounterFormSource(inputSet)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className={styles.typedFieldStatus}>
                                                                    {renderMappingStatusIcon(
                                                                        inputApplicationStatus === 'applied'
                                                                            ? 'applied'
                                                                            : 'mapped',
                                                                        formatEncounterFormSource(inputSet),
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                            {encounterFormInputPlan.skippedInputs.length > 0 && (
                                                <>
                                                    <h3 className={styles.subPanelTitle}>Skipped Inputs</h3>
                                                    <div className={styles.mappingFieldGrid}>
                                                        {encounterFormInputPlan.skippedInputs.map((input: any, index: number) => (
                                                            <div className={styles.typedFieldRow} key={`skipped-${index}`}>
                                                                <div className={styles.typedFieldLabel}>
                                                                    {input.label ||
                                                                        input.inputName ||
                                                                        input.inputMethodKey ||
                                                                        `Skipped ${index + 1}`}
                                                                </div>
                                                                <div className={styles.typedFieldValue}>
                                                                    {formatValue(input.reason)}
                                                                </div>
                                                                <div className={styles.typedFieldStatus}>
                                                                    {renderMappingStatusIcon('skipped', formatValue(input.reason))}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                            {!mappingEntities.length &&
                                                !encounterFormInputPlan.inputSets.length &&
                                                !encounterFormInputPlan.skippedInputs.length && (
                                                    <div className={styles.typedEmpty}>
                                                        No encounter form inputs were recorded.
                                                    </div>
                                                )}
                                        </>
                                    ) : (
                                        <>
                                            {encounterMappingRows.length > 0 && (
                                                <>
                                                    <div className={styles.mappingFieldGrid}>
                                                        {encounterMappingRows.map((field, index) => (
                                                            <div className={styles.typedFieldRow} key={`encounter-mapping-${index}`}>
                                                                <div className={styles.typedFieldLabel}>{field.label}</div>
                                                                <div className={styles.typedFieldValue}>
                                                                    {formatValue(field.value)}
                                                                </div>
                                                                <div className={styles.typedFieldStatus}>
                                                                    {renderMappingStatusIcon(field.status, field.reason)}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                            {!mappingEntities.length &&
                                                !encounterMappingRows.length &&
                                                entityUpdateSummary.updatedFieldCount === 0 && (
                                                    <div className={styles.typedEmpty}>
                                                        No encounter mapping was recorded.
                                                    </div>
                                                )}
                                        </>
                                    )}
                                </div>
                            )}

                            {selectedPanel === 'settings' && (
                                <div className={styles.detailPanel} data-test-id="typed-setup-panel">
                                    <div className={styles.panelHeaderRow}>
                                        <h2>Scan Settings</h2>
                                        <span>{typeDetail.facilityOverrideApplied ? 'Override' : 'Org default'}</span>
                                    </div>
                                    <div className={styles.typedFieldGrid}>
                                        {setupFields.map((field) => (
                                            <div className={styles.typedFieldRow} key={field.label}>
                                                <div className={styles.typedFieldLabel}>{field.label}</div>
                                                <div className={styles.typedFieldValue}>{field.value}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </aside>
                    </div>
                </div>
            </PageContent>

            <LlmTraceModal
                isOpen={traceModalOpen}
                onClose={() => setTraceModalOpen(false)}
                traceQuery={traceQuery}
            />
        </PageContainer>
    );
}
